import { cloneDeep } from "es-toolkit";
import { ZodError } from "zod";
import { Schedule } from "../types";
import { DeltaError } from "./errors";
import { Delta, Operation } from "./types";
import {
  applyAddFeature,
  applyAddRule,
  applyModifyRule,
  applyModifyFeature,
  applyRemoveFeature,
  applyRemoveRule,
  applyReorderRules,
} from "./operations";

type DeltaResult =
  | {
      success: true;
      schedule: Schedule;
      errors: [];
      operation: null;
    }
  | {
      success: false;
      schedule: null;
      errors: string[];
      operation: Operation;
    };

export function applyDelta(schedule: Schedule, delta: Delta): DeltaResult {
  let updated = cloneDeep(schedule);
  for (const op of delta.operations) {
    let result;
    try {
      result = _applyOperation(updated, op);
      if (result.success) {
        updated = result.schedule;
      }
    } catch (err) {
      if (err instanceof ZodError) {
        return {
          success: false,
          schedule: null,
          errors: [`Failed to apply operation ${op.kind}: schema failed during operation`, err.message],
          operation: op,
        };
      }
      // Unexpected type of error, let it bubble up
      throw err;
    }
    if (!result.success) {
      return {
        ...result,
        errors: [`Failed to apply operation ${op.kind}`, ...result.errors],
        operation: op,
      };
    }
  }
  return {
    success: true,
    schedule: updated,
    errors: [],
    operation: null,
  };
}

function _applyOperation(schedule: Schedule, op: Operation) {
  const kind = op.kind;
  switch (kind) {
    case "add_feature":
      return applyAddFeature(schedule, op);
    case "modify_feature":
      return applyModifyFeature(schedule, op);
    case "remove_feature":
      return applyRemoveFeature(schedule, op);
    case "add_rule":
      return applyAddRule(schedule, op);
    case "modify_rule":
      return applyModifyRule(schedule, op);
    case "remove_rule":
      return applyRemoveRule(schedule, op);
    case "reorder_rules":
      return applyReorderRules(schedule, op);
    default: {
      const _exhaustive: never = kind;
      throw new DeltaError(`Unknown operation ${JSON.stringify(_exhaustive)}`);
    }
  }
}
